import React, { useState, useEffect, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Tag, PlusCircle, Edit, Power, PowerOff, Copy, ListTree, Calendar as CalendarIcon,
  Percent, Coins, Users,
} from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = {
  code: '',
  discount_type: 'percentage',
  discount_value: '',
  applies_to_all: true,
  event_ids: [],
  usage_cap: '',
  expires_at: '',
  is_active: true,
};

export function AdminPromoCodesTab() {
  const [codes, setCodes] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });

  const [drillDown, setDrillDown] = useState(null); // { code, redemptions }

  useEffect(() => { loadCodes(); loadEvents(); }, []);

  const loadCodes = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/admin/promo-codes', { credentials: 'include' });
      if (res.ok) setCodes(await res.json());
    } catch { /* toast handled inline where useful */ }
    setLoading(false);
  };

  const loadEvents = async () => {
    try {
      const res = await fetch('/api/events', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setEvents(data.filter(e => (e.ticket_price || 0) > 0));
      }
    } catch { /* no-op */ }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm });
    setShowForm(true);
  };

  const openEdit = (code) => {
    setEditing(code);
    setForm({
      code: code.code,
      discount_type: code.discount_type,
      discount_value: String(code.discount_value ?? ''),
      applies_to_all: !!code.applies_to_all,
      event_ids: code.event_ids || [],
      usage_cap: code.usage_cap == null ? '' : String(code.usage_cap),
      expires_at: code.expires_at ? code.expires_at.slice(0, 10) : '',
      is_active: !!code.is_active,
    });
    setShowForm(true);
  };

  const handleSave = async () => {
    const trimmed = form.code.trim();
    if (trimmed.length < 2) { toast.error('Code must be at least 2 characters.'); return; }
    const val = parseFloat(form.discount_value);
    if (!val || val <= 0) { toast.error('Discount value must be greater than zero.'); return; }
    if (form.discount_type === 'percentage' && (val > 100)) {
      toast.error('Percentage codes must be between 1 and 100.'); return;
    }
    if (!form.applies_to_all && form.event_ids.length === 0) {
      toast.error('Pick at least one event or set the code to apply to all events.'); return;
    }

    const payload = {
      code: trimmed,
      discount_type: form.discount_type,
      discount_value: val,
      applies_to_all: form.applies_to_all,
      event_ids: form.applies_to_all ? [] : form.event_ids,
      usage_cap: form.usage_cap === '' ? null : parseInt(form.usage_cap, 10),
      // Send midnight UTC of the chosen day so "expires 2026-03-15" is well-defined
      expires_at: form.expires_at ? new Date(form.expires_at + 'T23:59:59Z').toISOString() : null,
      is_active: form.is_active,
    };

    try {
      const url = editing
        ? `/api/admin/promo-codes/${editing.promo_id}`
        : '/api/admin/promo-codes';
      const method = editing ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not save code');
      toast.success(editing ? 'Code updated' : 'Code created');
      setShowForm(false);
      loadCodes();
    } catch (e) { toast.error(e.message); }
  };

  const handleToggleActive = async (code) => {
    // Reuse the update endpoint so we flip is_active without deactivating history
    const payload = {
      code: code.code,
      discount_type: code.discount_type,
      discount_value: code.discount_value,
      applies_to_all: code.applies_to_all,
      event_ids: code.event_ids || [],
      usage_cap: code.usage_cap,
      expires_at: code.expires_at,
      is_active: !code.is_active,
    };
    try {
      const res = await fetch(`/api/admin/promo-codes/${code.promo_id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error();
      toast.success(code.is_active ? 'Code deactivated' : 'Code activated');
      loadCodes();
    } catch { toast.error('Could not update code'); }
  };

  const handleCopy = async (code) => {
    try { await navigator.clipboard.writeText(code.code); toast.success(`Copied "${code.code}"`); }
    catch { toast.error('Could not copy'); }
  };

  const openDrill = async (code) => {
    try {
      const res = await fetch(`/api/admin/promo-codes/${code.promo_id}/redemptions`, { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error();
      setDrillDown(data);
    } catch { toast.error('Could not load redemptions'); }
  };

  const toggleEvent = (event_id) => {
    setForm(prev => ({
      ...prev,
      event_ids: prev.event_ids.includes(event_id)
        ? prev.event_ids.filter(id => id !== event_id)
        : [...prev.event_ids, event_id],
    }));
  };

  const eventLabelById = useMemo(() => {
    const m = {};
    for (const ev of events) m[ev.event_id] = ev.title;
    return m;
  }, [events]);

  const statusPill = (code) => {
    if (!code.is_active) return <span className="px-2 py-0.5 text-xs rounded-full bg-slate-200 text-slate-600" data-testid={`promo-status-${code.promo_id}`}>Inactive</span>;
    if (code.expires_at && new Date(code.expires_at) < new Date()) return <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-700" data-testid={`promo-status-${code.promo_id}`}>Expired</span>;
    if (code.usage_cap != null && (code.usage_count || 0) >= code.usage_cap) return <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700" data-testid={`promo-status-${code.promo_id}`}>Cap reached</span>;
    return <span className="px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700" data-testid={`promo-status-${code.promo_id}`}>Live</span>;
  };

  return (
    <div className="space-y-6" data-testid="admin-promo-codes-tab">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-heading font-bold text-slate-800 flex items-center gap-2">
            <Tag className="h-6 w-6 text-brand-navy" /> Promo Codes
          </h2>
          <p className="text-sm text-slate-500 mt-1">Issue discount codes for paid events. Codes are case-insensitive and one code applies per checkout.</p>
        </div>
        <Button data-testid="promo-new-btn" onClick={openCreate} className="bg-brand-navy hover:bg-brand-dark text-white">
          <PlusCircle className="h-4 w-4 mr-2" /> New Code
        </Button>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading…</div>
        ) : codes.length === 0 ? (
          <div className="p-8 text-center text-slate-500" data-testid="promo-empty-state">
            No promo codes yet. Create your first one to start rewarding attendees.
          </div>
        ) : (
          <div className="divide-y">
            {codes.map(code => {
              const scopeLabel = code.applies_to_all
                ? 'All paid events'
                : `${(code.event_ids || []).length} event${(code.event_ids || []).length === 1 ? '' : 's'}`;
              const usesLabel = code.usage_cap == null
                ? `${code.usage_count || 0} uses`
                : `${code.usage_count || 0} / ${code.usage_cap} used`;
              const discountLabel = code.discount_type === 'percentage'
                ? `${code.discount_value}% off`
                : `£${Number(code.discount_value).toFixed(2)} off`;
              return (
                <div key={code.promo_id} className="p-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-6" data-testid={`promo-row-${code.promo_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-base font-semibold text-slate-800" data-testid={`promo-code-${code.promo_id}`}>{code.code}</span>
                      {statusPill(code)}
                      <button onClick={() => handleCopy(code)} className="text-slate-400 hover:text-brand-navy transition" data-testid={`promo-copy-${code.promo_id}`} title="Copy code">
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="mt-1 text-sm text-slate-600 flex flex-wrap items-center gap-x-4 gap-y-1">
                      <span className="inline-flex items-center gap-1">
                        {code.discount_type === 'percentage' ? <Percent className="h-3.5 w-3.5" /> : <Coins className="h-3.5 w-3.5" />}
                        {discountLabel}
                      </span>
                      <span className="inline-flex items-center gap-1"><ListTree className="h-3.5 w-3.5" />{scopeLabel}</span>
                      <span className="inline-flex items-center gap-1"><Users className="h-3.5 w-3.5" />{usesLabel}</span>
                      {code.expires_at && (
                        <span className="inline-flex items-center gap-1"><CalendarIcon className="h-3.5 w-3.5" />Expires {new Date(code.expires_at).toLocaleDateString('en-GB')}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button variant="outline" size="sm" onClick={() => openDrill(code)} data-testid={`promo-uses-${code.promo_id}`}>Uses</Button>
                    <Button variant="outline" size="sm" onClick={() => openEdit(code)} data-testid={`promo-edit-${code.promo_id}`}>
                      <Edit className="h-4 w-4 mr-1" /> Edit
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleToggleActive(code)} data-testid={`promo-toggle-${code.promo_id}`}>
                      {code.is_active ? <><PowerOff className="h-4 w-4 mr-1" /> Deactivate</> : <><Power className="h-4 w-4 mr-1" /> Activate</>}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Promo Code' : 'New Promo Code'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="promo-code-input">Code</Label>
              <Input
                id="promo-code-input"
                data-testid="promo-form-code"
                value={form.code}
                onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="WELCOME10"
                className="uppercase font-mono"
              />
            </div>

            <div>
              <Label>Discount</Label>
              <div className="flex items-center gap-2 mt-1">
                <select
                  data-testid="promo-form-discount-type"
                  className="px-3 py-2 rounded-md border border-slate-200 bg-white text-sm"
                  value={form.discount_type}
                  onChange={e => setForm({ ...form, discount_type: e.target.value })}
                >
                  <option value="percentage">% off</option>
                  <option value="fixed">£ off</option>
                </select>
                <Input
                  data-testid="promo-form-discount-value"
                  type="number"
                  step="0.01"
                  value={form.discount_value}
                  onChange={e => setForm({ ...form, discount_value: e.target.value })}
                  placeholder={form.discount_type === 'percentage' ? '10' : '20.00'}
                />
              </div>
              <p className="text-xs text-slate-400 mt-1">
                {form.discount_type === 'percentage' ? 'Whole or fractional percent, 1–100.' : 'Fixed GBP amount off the ticket total.'}
              </p>
            </div>

            <div className="flex items-center justify-between p-3 rounded-md border border-slate-100 bg-slate-50">
              <div>
                <Label className="!m-0">Applies to all paid events</Label>
                <p className="text-xs text-slate-500">Off → pick specific events below.</p>
              </div>
              <Switch
                data-testid="promo-form-applies-all"
                checked={form.applies_to_all}
                onCheckedChange={v => setForm({ ...form, applies_to_all: v })}
              />
            </div>

            {!form.applies_to_all && (
              <div>
                <Label>Events</Label>
                <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-slate-200 divide-y" data-testid="promo-form-events-list">
                  {events.length === 0 && (
                    <div className="p-3 text-sm text-slate-500">No paid events available.</div>
                  )}
                  {events.map(ev => (
                    <label key={ev.event_id} className="flex items-center gap-2 p-2 text-sm cursor-pointer hover:bg-slate-50">
                      <input
                        type="checkbox"
                        checked={form.event_ids.includes(ev.event_id)}
                        onChange={() => toggleEvent(ev.event_id)}
                        data-testid={`promo-form-event-${ev.event_id}`}
                      />
                      <span className="flex-1 truncate">{ev.title}</span>
                      <span className="text-slate-400 text-xs">£{Number(ev.ticket_price).toFixed(2)}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="promo-cap">Usage cap</Label>
                <Input
                  id="promo-cap"
                  data-testid="promo-form-cap"
                  type="number"
                  value={form.usage_cap}
                  onChange={e => setForm({ ...form, usage_cap: e.target.value })}
                  placeholder="Unlimited"
                />
              </div>
              <div>
                <Label htmlFor="promo-expiry">Expires</Label>
                <Input
                  id="promo-expiry"
                  data-testid="promo-form-expiry"
                  type="date"
                  value={form.expires_at}
                  onChange={e => setForm({ ...form, expires_at: e.target.value })}
                />
              </div>
            </div>

            <div className="flex items-center justify-between p-3 rounded-md border border-slate-100 bg-slate-50">
              <Label className="!m-0">Active</Label>
              <Switch
                data-testid="promo-form-active"
                checked={form.is_active}
                onCheckedChange={v => setForm({ ...form, is_active: v })}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowForm(false)} data-testid="promo-form-cancel">Cancel</Button>
              <Button onClick={handleSave} data-testid="promo-form-save" className="bg-brand-navy hover:bg-brand-dark text-white">
                {editing ? 'Save changes' : 'Create code'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Redemption drill-in */}
      <Dialog open={!!drillDown} onOpenChange={(v) => !v && setDrillDown(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {drillDown?.code?.code} — Redemptions
            </DialogTitle>
          </DialogHeader>
          {drillDown?.redemptions?.length ? (
            <div className="divide-y">
              {drillDown.redemptions.map(r => (
                <div key={r.redemption_id} className="py-3 text-sm" data-testid={`redemption-row-${r.redemption_id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-800 truncate">{r.user_name || r.user_email}</div>
                      <div className="text-xs text-slate-500 truncate">{r.event_title}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-slate-500 line-through text-xs">£{Number(r.original_amount).toFixed(2)}</div>
                      <div className="font-semibold text-slate-800">£{Number(r.final_amount).toFixed(2)}</div>
                      <div className="text-xs text-emerald-600">–£{Number(r.discount_amount).toFixed(2)}</div>
                    </div>
                  </div>
                  <div className="mt-1 text-xs text-slate-400">{new Date(r.redeemed_at).toLocaleString('en-GB')}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-slate-500">No redemptions yet.</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminPromoCodesTab;
